import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import { environmentHttpClient } from "client/Api/httpClient";

export enum LiveTileHeaderStyle {
	coloredText = 0,
	coloredBackground = 1,
}

export enum WorkspaceHeaderStyle {
	coloredBackground = 0,
	whiteBackground = 1
}

export type BrandingLogoModel = OptionalModel<Base64FileUriUploadModel | null>;

export type DarkThemeSettingsSaveModel = {
	primaryColor: string | null;
	textLinkColor: string | null;
	workspaceLogo: BrandingLogoModel;
	publicPageLogo: BrandingLogoModel;
	homeLogo: BrandingLogoModel;
};

export type DarkThemeSettingsModel = {
	primaryColor: string | null;
	textLinkColor: string | null;
	workspaceLogoId: string | null;
	workspaceSignInLogoId: string | null;
	homeLogoId: string | null;
};

export type BrandingSettingsViewModel = {
	id: number | null;
	brandingName: string;
	productName: string;
	primaryColor: string | null;
	textLinkColor: string | null;
	publicPageLogoId: string | null;
	headerColor: string | null;
	workspaceLogoId: string | null;
	workspaceBackgroundId: string | null;
	homeLogoId: string | null;
	supportContactDetails: string;
	customHomeButtonUrl: string;
	isDefault: boolean;
	liveTileHeaderStyle: LiveTileHeaderStyle;
	workspaceHeaderStyle: WorkspaceHeaderStyle;
	targetAudience: BrandingTargetAudienceModel;
	defaultBranding: BrandingSettingsListItemModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	darkThemeSettings: DarkThemeSettingsModel;
	contrastColorStyle: ContrastColorStyle;
};

export type BrandingSettingsSaveModel = {
	brandingName: string;
	productName: string;
	primaryColor: string | null;
	textLinkColor: string | null;
	headerColor: string | null;
	workspaceLogo: BrandingLogoModel;
	publicPageLogo: BrandingLogoModel;
	homeLogo: BrandingLogoModel;
	workspaceBackground: BrandingLogoModel;
	supportContactDetails: string;
	customHomeButtonUrl: string | null;
	isDefault: boolean;
	liveTileHeaderStyle: LiveTileHeaderStyle;
	workspaceHeaderStyle: WorkspaceHeaderStyle;
	targetAudience: BrandingTargetAudienceModel | null;
	darkThemeSettings: DarkThemeSettingsSaveModel;
	contrastColorStyle: ContrastColorStyle;
};

export type BrandingSettingsSaveResultModel = {
	id: number;
};

export type BrandingTargetAudienceModel = {
	userIds: number[];
	userGroupIds: number[];
};

export enum ContrastColorStyle {
	Light = 0,
	Optimal = 1
};

export type BrandingSettingsListItemModel = {
	id: number;
	brandingName: string;
	isDefault: boolean;
};

export type BrandingSettingsListModel = {
	brandingSettingsItems: BrandingSettingsListItemModel[];
};

export type BrandingViewModelConfig = {
	model: BrandingSettingsViewModel;
	logoConstraints: BrandingImageConstraints;
	backgroundConstraints: BrandingImageConstraints;
	isCustomHomeButtonUrlEnabled: boolean;
};

export type BrandingSettingsListConfig = {
	model: BrandingSettingsListModel;
};

export type BrandingImageConstraints = {
	supportedImageTypes: string[];
	maxFileSize: number;
};

export type ThemeDictionary<TValue> = {
	light: TValue;
	dark: TValue;
};

export function list(signal?: AbortSignal): Promise<BrandingSettingsListModel> {
	return environmentHttpClient.get<BrandingSettingsListModel>(
		"/UiApi/Branding",
		undefined,
		{ signal }
	);
}

export function getCreateViewModel(signal?: AbortSignal): Promise<BrandingViewModelConfig> {
	return environmentHttpClient.get<BrandingViewModelConfig>(
		"/UiApi/Branding/Add",
		undefined,
		{ signal }
	);
}

export function getEditViewModel(id: number, signal?: AbortSignal): Promise<BrandingViewModelConfig> {
	return environmentHttpClient.get<BrandingViewModelConfig>(
		`/UiApi/Branding/Edit/${id}`,
		undefined,
		{ signal }
	);
}

export function addBranding(
	brandingSettingsModel: BrandingSettingsSaveModel,
	signal?: AbortSignal
): Promise<BrandingSettingsSaveResultModel> {
	return environmentHttpClient.post<BrandingSettingsSaveResultModel>(
		"/UiApi/Branding/Add",
		brandingSettingsModel,
		{ signal }
	);
}

export function updateBranding(
	id: number | null,
	brandingSettingsModel: BrandingSettingsSaveModel,
	signal?: AbortSignal
): Promise<BrandingSettingsSaveResultModel> {
	return environmentHttpClient.put<BrandingSettingsSaveResultModel>(
		`/UiApi/Branding/Edit/${id}`,
		brandingSettingsModel,
		{ signal }
	);
}

export function deleteBranding(
	brandingIds: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		"/UiApi/Branding/Delete",
		brandingIds,
		{ signal }
	);
}